import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import type { BreadcrumbItem } from '@/components/base/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'techniques');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSlug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.category === activeSlug),
    [activeSlug]
  );

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: '首页', href: '/' },
    { label: currentCategory?.name || '分类' },
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: `${currentCategory?.name || ''} - 乳交完全指南`,
        description: currentCategory?.description || '',
        url: `https://zhuoyingcaiwu.cn/category/${activeSlug}`,
        inLanguage: 'zh-CN',
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="px-4 md:px-6 py-6 md:py-8 max-w-6xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            {/* Category Header */}
            <div className="mb-8">
              <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-2">
                {currentCategory?.name}
              </h1>
              <p className="text-sm text-foreground-500 max-w-xl">
                {currentCategory?.description}
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-nowrap overflow-x-auto gap-2 mb-8 pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all duration-200 cursor-pointer flex-shrink-0 ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Articles Grid */}
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <ArticleCard
                    key={article.slug}
                    slug={article.slug}
                    title={article.title}
                    excerpt={article.excerpt}
                    category={article.category}
                    readTime={article.readTime}
                    publishedAt={article.publishedAt}
                    imageUrl={article.imageUrl}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-foreground-500 text-sm">
                  该分类暂无文章，请浏览其他分类。
                </p>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}